import { ajax } from '@egr/wcf/modules/utils/async';
import { isNotNullOrEmpty } from '@egr/wcf/modules/utils/string';
import { getEnvVar } from '@egr/xbox/utils/ReactScriptHelper';
import { ignoreAjaxErrorHandlerMessageCallback } from '@egr/xbox/utils/AjaxErrorHandler';

const OWDS_BASEURL: string = getEnvVar('OWDS_BASEURL', 'https://owds.pcon-solutions.com/api');

interface SharedFile {
    url: string;
    expirationTimestamp: string;
}

interface OWDSUploadInformation {
    filename: string;
    content: ArrayBuffer | Blob;
    accessToken?: string;
    purpose?: string;
}

interface CreateFileResponse {
    file_upload_token: string;
    file_expiration: string;
}

function createFile(uploadInfo: OWDSUploadInformation): Promise<CreateFileResponse> {
    const fileSize = uploadInfo.content instanceof Blob ? uploadInfo.content.size : uploadInfo.content.byteLength;
    const headers = isNotNullOrEmpty(uploadInfo.accessToken) ? {
        'Authorization': `Bearer ${uploadInfo.accessToken}`
    } : undefined;

    return ajax<CreateFileResponse>(
        'POST',
        `${OWDS_BASEURL}/v1/shared_files`,
        {
            file_name: uploadInfo.filename,
            file_size: fileSize,
            purpose: uploadInfo.purpose
        },
        {
            dataType: 'json',
            responseType: 'json',
            retryAttempts: 0,
            headers
        },
        ignoreAjaxErrorHandlerMessageCallback
    );
}

async function uploadFile(uploadInfo: OWDSUploadInformation, createFileResponse: CreateFileResponse): Promise<string> {
    const response = await ajax<{uri: string}>(
        'POST',
        `${OWDS_BASEURL}/v2/shared_files/upload/${createFileResponse.file_upload_token}`,
        uploadInfo.content,
        {
            responseType: 'json',
            retryAttempts: 0,
        },
        ignoreAjaxErrorHandlerMessageCallback
    );
    return response.uri;
}

export async function shared_files(uploadInfo: OWDSUploadInformation): Promise<SharedFile> {
    const createFileResponse = await createFile(uploadInfo);
    const url = await uploadFile(uploadInfo, createFileResponse);

    return {
        url,
        expirationTimestamp: createFileResponse.file_expiration
    };
}

/**
 * should be used for clientside generated content
 */
export const getOWDSShareFile = shared_files;
export const getOWDSShareFileUrl = async (uploadInfo: OWDSUploadInformation): Promise<string> => {
    const response = await shared_files(uploadInfo);
    return response.url;
};